/**
 * RecentlyViewedWidget — top-5 recently viewed pages for the current user.
 * Reads from LocalStorage first (instant) and hydrates from the backend
 * so the trail follows the user across devices.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Clock, X } from "lucide-react";
import { toast } from "sonner";

export default function RecentlyViewedWidget() {
  const { activeWorkspaceId, user } = useAuth();
  const [items, setItems] = useState([]);

  const load = () => {
    if (!activeWorkspaceId || !user?.id) return;
    // 1) LocalStorage first for instant paint
    const key = `acquilix.recent.${activeWorkspaceId}.${user.id}`;
    try {
      const list = JSON.parse(localStorage.getItem(key) || "[]");
      setItems(list.slice(0, 5));
    } catch { /* ok */ }

    // 2) Then hydrate from backend (cross-device sync)
    api.get(`/workspaces/${activeWorkspaceId}/recent-pages?limit=5`)
      .then(({ data }) => {
        if (Array.isArray(data) && data.length > 0) setItems(data.slice(0, 5));
      })
      .catch(() => { /* silent */ });
  };

  useEffect(() => { load(); }, [activeWorkspaceId, user?.id]);

  useEffect(() => {
    const onUpdate = () => load();
    window.addEventListener("recent-pages:updated", onUpdate);
    return () => window.removeEventListener("recent-pages:updated", onUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId, user?.id]);

  const clearAll = async () => {
    if (!confirm("Clear your recently-viewed pages?")) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/recent-pages`);
      const key = `acquilix.recent.${activeWorkspaceId}.${user.id}`;
      localStorage.removeItem(key);
      setItems([]);
      toast.success("Cleared");
    } catch { toast.error("Could not clear"); }
  };

  if (!items.length) return null;

  return (
    <section className="border border-border" data-testid="recently-viewed-widget">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2 bg-muted/40">
        <Clock className="w-4 h-4" />
        <div className="label-eyebrow">Recently viewed</div>
        <button
          type="button"
          onClick={clearAll}
          className="ml-auto text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
          data-testid="recent-clear-btn"
          title="Clear history"
        >
          <X className="w-3 h-3" /> Clear
        </button>
      </div>
      <ul className="divide-y divide-border">
        {items.map((r) => (
          <li key={r.path} data-testid={`recent-page-${encodeURIComponent(r.path)}`}>
            <Link
              to={r.path}
              className="flex items-center gap-2 px-4 py-2 hover:bg-muted/30 transition text-sm"
            >
              <span className="text-base leading-none">{r.icon || "📄"}</span>
              <span className="truncate flex-1">{r.title}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
